/**
 * 阿里云实时语音识别（NLS WebSocket），逻辑对齐 demo.html：
 * StartTranscription → TranscriptionStarted → 采集 PCM →
 * TranscriptionResultChanged（中间结果）/ SentenceEnd（一句最终 result）
 * @see src/components/student/liveRoom/demo.html
 */

function generateTaskId() {
    return "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
}

function float32ToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return int16Array;
}

export default class AliyunNlsSpeechTranscriber {
    /**
     * @param {object} options
     * @param {string} options.url - wss 网关，不含 query
     * @param {string} options.appkey
     * @param {() => string | Promise<string>} options.getToken
     * @param {() => void} [options.onTranscriptionStarted]
     * @param {(text: string) => void} [options.onIntermediate]
     * @param {(text: string) => void} [options.onSentenceEnd]
     * @param {() => void} [options.onTranscriptionCompleted]
     * @param {(message: object) => void} [options.onTaskFailed]
     * @param {(ev: Event) => void} [options.onWsError]
     * @param {(ev: CloseEvent) => void} [options.onWsClose]
     */
    constructor(options) {
        this.urlBase = options.url || "wss://nls-gateway-cn-shanghai.aliyuncs.com/ws/v1";
        this.appkey = options.appkey || "";
        this.getToken = options.getToken;
        this.onTranscriptionStarted = options.onTranscriptionStarted || function () {};
        this.onIntermediate = options.onIntermediate || function () {};
        this.onSentenceEnd = options.onSentenceEnd || function () {};
        this.onTranscriptionCompleted = options.onTranscriptionCompleted || function () {};
        this.onTaskFailed = options.onTaskFailed || function () {};
        this.onWsError = options.onWsError || function () {};
        this.onWsClose = options.onWsClose || function () {};

        this.websocket = null;
        this.audioContext = null;
        this.mediaStream = null;
        this.scriptProcessor = null;
        this.isRecording = false;
        this.taskId = null;
        /** @type {Promise<void> | null} */
        this._startedPromise = null;
        this._startedResolve = null;
        this._startedReject = null;
        /** WebSocket 已连上但尚未收到 TranscriptionStarted */
        this._awaitingTranscriptionStart = false;
    }

    async start() {
        const token = typeof this.getToken === "function" ? await Promise.resolve(this.getToken()) : this.getToken;
        if (!token || !String(token).trim()) {
            throw new Error("ALIYUN_NLS_NO_TOKEN");
        }
        if (!this.appkey) {
            throw new Error("ALIYUN_NLS_NO_APPKEY");
        }

        this.mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                sampleRate: 16000,
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
            },
        });

        this.taskId = generateTaskId();
        this._awaitingTranscriptionStart = true;
        this._startedPromise = new Promise((resolve, reject) => {
            this._startedResolve = resolve;
            this._startedReject = reject;
        });

        const wsUrl = `${this.urlBase}?token=${encodeURIComponent(String(token).trim())}`;
        this.websocket = new WebSocket(wsUrl);

        this.websocket.onopen = () => {
            const startParams = {
                header: {
                    message_id: generateTaskId(),
                    task_id: this.taskId,
                    namespace: "SpeechTranscriber",
                    name: "StartTranscription",
                    appkey: this.appkey,
                },
                payload: {
                    format: "pcm",
                    sample_rate: 16000,
                    enable_intermediate_result: true,
                    enable_punctuation_prediction: true,
                    enable_inverse_text_normalization: true,
                },
            };
            this.websocket.send(JSON.stringify(startParams));
        };

        this.websocket.onmessage = (event) => {
            if (typeof event.data !== "string") return;
            let message;
            try {
                message = JSON.parse(event.data);
            } catch (e) {
                return;
            }
            const headerName = message.header && message.header.name;
            switch (headerName) {
                case "TranscriptionStarted":
                    this._awaitingTranscriptionStart = false;
                    this.isRecording = true;
                    this.startAudioCapture();
                    this.onTranscriptionStarted(message);
                    if (this._startedResolve) {
                        this._startedResolve();
                        this._startedResolve = null;
                        this._startedReject = null;
                    }
                    break;
                case "TranscriptionResultChanged": {
                    const intermediateText = message.payload && message.payload.result;
                    this.onIntermediate(intermediateText || "");
                    break;
                }
                case "SentenceEnd": {
                    const finalText = message.payload && message.payload.result;
                    this.onSentenceEnd(finalText || "");
                    break;
                }
                case "TranscriptionCompleted":
                    this.onTranscriptionCompleted(message);
                    this._releaseMedia();
                    if (this.websocket) {
                        try {
                            this.websocket.close();
                        } catch (e) {
                            /* ignore */
                        }
                        this.websocket = null;
                    }
                    break;
                case "TaskFailed":
                    this._awaitingTranscriptionStart = false;
                    this.onTaskFailed(message);
                    if (this._startedReject) {
                        this._startedReject(new Error(message.header && message.header.status_text));
                        this._startedResolve = null;
                        this._startedReject = null;
                    }
                    this._releaseMedia();
                    if (this.websocket) {
                        try {
                            this.websocket.close();
                        } catch (e) {
                            /* ignore */
                        }
                        this.websocket = null;
                    }
                    break;
                default:
                    break;
            }
        };

        this.websocket.onerror = (err) => {
            this._awaitingTranscriptionStart = false;
            this.onWsError(err);
            if (this._startedReject) {
                this._startedReject(err);
                this._startedResolve = null;
                this._startedReject = null;
            }
            this._releaseMedia();
        };

        this.websocket.onclose = (event) => {
            this.onWsClose(event);
            if (this._awaitingTranscriptionStart && this._startedReject) {
                this._startedReject(new Error("WS_CLOSED_BEFORE_TRANSCRIPTION_STARTED"));
                this._startedResolve = null;
                this._startedReject = null;
            }
            this._awaitingTranscriptionStart = false;
            this.isRecording = false;
            this._releaseMedia();
            this.websocket = null;
        };

        return this._startedPromise;
    }

    startAudioCapture() {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 16000,
        });
        const source = this.audioContext.createMediaStreamSource(this.mediaStream);
        this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);
        this.scriptProcessor.onaudioprocess = (event) => {
            if (!this.isRecording || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
                return;
            }
            const float32Data = event.inputBuffer.getChannelData(0);
            const int16Data = float32ToInt16(float32Data);
            this.websocket.send(int16Data.buffer);
        };
        source.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.audioContext.destination);
    }

    stop() {
        this.isRecording = false;
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN && this.taskId) {
            const stopParams = {
                header: {
                    message_id: generateTaskId(),
                    task_id: this.taskId,
                    namespace: "SpeechTranscriber",
                    name: "StopTranscription",
                    appkey: this.appkey,
                },
            };
            try {
                this.websocket.send(JSON.stringify(stopParams));
            } catch (e) {
                this.destroy();
            }
        } else {
            this.destroy();
        }
    }

    _releaseMedia() {
        if (this.scriptProcessor) {
            try {
                this.scriptProcessor.disconnect();
            } catch (e) {
                /* ignore */
            }
            this.scriptProcessor = null;
        }
        if (this.audioContext) {
            try {
                this.audioContext.close();
            } catch (e) {
                /* ignore */
            }
            this.audioContext = null;
        }
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach((track) => track.stop());
            this.mediaStream = null;
        }
    }

    /** 释放所有资源（页面卸载 / 停止直播） */
    destroy() {
        this.isRecording = false;
        if (this.websocket) {
            try {
                if (this.websocket.readyState === WebSocket.OPEN && this.taskId) {
                    const stopParams = {
                        header: {
                            message_id: generateTaskId(),
                            task_id: this.taskId,
                            namespace: "SpeechTranscriber",
                            name: "StopTranscription",
                            appkey: this.appkey,
                        },
                    };
                    this.websocket.send(JSON.stringify(stopParams));
                }
            } catch (e) {
                /* ignore */
            }
            try {
                this.websocket.close();
            } catch (e) {
                /* ignore */
            }
            this.websocket = null;
        }
        this._releaseMedia();
        this.taskId = null;
    }
}
